/**
 * Get the overflow directions of a given container
 *
 * @param {HTMLElement} scrollContainer - the scrolling container
 * @param {number} threshold - the threshold in pixels that we start indicating overflow
 * @returns {object} the { top, right, bottom, left } overflow booleans (true if overflowing)
 */
export const getOverflowDirections = (scrollContainer, threshold = 20) => {
  const isWindowScroll = scrollContainer === window
  const containerRect = isWindowScroll
    ? document.documentElement
    : scrollContainer
  const scrollRect = containerRect.getBoundingClientRect()
  const contentWidth = containerRect.scrollWidth
  const contentHeight = containerRect.scrollHeight
  const scrollWidth = scrollRect.width
  const scrollHeight = scrollRect.height
  const scrollLeft = isWindowScroll
    ? window.pageXOffset
    : containerRect.scrollLeft
  const scrollTop = isWindowScroll
    ? window.pageYOffset
    : containerRect.scrollTop

  return {
    top: scrollTop > threshold,
    bottom: contentHeight - scrollHeight - scrollTop > threshold,
    left: scrollLeft > threshold,
    right: contentWidth - scrollWidth - scrollLeft > threshold
  }
}
/**
 * Get the nearest scrolling container
 *
 * @param {HTMLElement} element - the element
 * @returns {HTMLElement} the nearest scrolling container
 */
export const getNearestScrollingContainer = element => {
  while (element != null && element.tagName !== 'BODY') {
    const style = getComputedStyle(element)

    if (/auto|scroll/.test(style.overflow)) {
      return element
    }

    element = element.parentElement
  }

  return window
}

/**
 * An extension of the native `scrollIntoView`, this utility
 * allows us to apply a threshold to whether or not the element
 * should be scrolled into the center
 *
 * @param {HTMLElement} element - the element
 * @param {number} thresholdX - the x threshold
 * @param {number} thresholdY - the y threshold
 */
export const scrollIntoViewCenter = (
  element,
  thresholdX = 0.2,
  thresholdY = 0.15
) => {
  const nearestScroll = getNearestScrollingContainer(element)

  if (nearestScroll === window) {
    // Not in a scrolling container
    return
  }

  const rectElement = element.getBoundingClientRect()
  const rectScroll = nearestScroll.getBoundingClientRect()
  const { overflow } = getComputedStyle(nearestScroll)
  const { bottom, left, right, top } = getOverflowDirections(nearestScroll, 0)
  const [isOverflowX, isOverflowY] = overflow
    .split(' ')
    .map(value => /auto|scroll/.test(value))

  let shouldCenter = false
  const topThreshold = (rectElement.top - rectScroll.top) / rectScroll.height
  const bottomThreshold =
    (rectScroll.bottom - rectElement.bottom) / rectScroll.height
  const leftThreshold = (rectElement.left - rectScroll.left) / rectScroll.width
  const rightThreshold =
    (rectScroll.right - rectElement.right) / rectScroll.width

  // Note: Also using the `getOverflowDirections` as `scrollIntoView` can slightly
  // nudge even if there is nowhere for it to scroll (its overscrolling), defend against
  if (isOverflowX) {
    // Has X overflow
    if (
      (leftThreshold < thresholdX && left) ||
      (rightThreshold < thresholdX && right)
    ) {
      shouldCenter = true
    }
  }

  if (isOverflowY) {
    // Has Y overflow
    if (
      (topThreshold < thresholdY && top) ||
      (bottomThreshold < thresholdY && bottom)
    ) {
      shouldCenter = true
    }
  }

  if (!shouldCenter) {
    // Skip
    return
  }

  if (!top && !right && !bottom && !left) {
    return
  }

  element.scrollIntoView({
    behavior: 'smooth',
    block: top || bottom ? 'center' : 'nearest',
    inline: left || right ? 'center' : 'nearest'
  })
}
